import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Alert, Box, Button, Link as MuiLink, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthShell } from './AuthShell';
import { completePasswordReset, requestPasswordReset } from '../../lib/auth/passwordClient';

/**
 * Dual-mode page. Without query params: asks for email and triggers a
 * reset email. With ?userId=&code=: shows the new-password form.
 */
export function PasswordReset() {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const code = searchParams.get('code');
  const hasCodeParams = Boolean(userId && code);

  return (
    <AuthShell>{hasCodeParams ? <CompleteForm userId={userId!} code={code!} /> : <RequestForm />}</AuthShell>
  );
}

function RequestForm() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await requestPasswordReset(email.trim());
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <Stack spacing={2}>
        <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Check your email
        </Typography>
        <Typography variant="body2" color="text.secondary">
          If an account exists for <strong>{email}</strong>, we've sent a password reset link. It will expire in 2 days.
        </Typography>
        <MuiLink component={RouterLink} to="/login" variant="body2">
          Back to sign in
        </MuiLink>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={0.5}>
        <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Reset your password
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Enter your email and we'll send you a link to reset it.
        </Typography>
      </Stack>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2}>
          <TextField
            label="Email"
            type="email"
            required
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
          />
          <Button type="submit" variant="contained" size="large" disabled={submitting || !email}>
            {submitting ? 'Sending…' : 'Send reset link'}
          </Button>
          <MuiLink component={RouterLink} to="/login" variant="body2" sx={{ alignSelf: 'flex-start' }}>
            Back to sign in
          </MuiLink>
        </Stack>
      </Box>
    </Stack>
  );
}

function CompleteForm({ userId, code }: { userId: string; code: string }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const validation = useMemo(() => validatePassword(password, confirm), [password, confirm]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (validation) {
      setError(validation);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await completePasswordReset(userId, code, password);
      navigate('/login?reset=1', { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err) ?? 'Unable to reset password. The link may have expired.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={0.5}>
        <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Choose a new password
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Minimum 8 characters.
        </Typography>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2}>
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            required
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
          />
          <TextField
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            required
            fullWidth
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={submitting}
          />
          <Button type="submit" variant="contained" size="large" disabled={submitting || !password || !confirm}>
            {submitting ? 'Resetting…' : 'Reset password'}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}

function validatePassword(pw: string, confirm: string): string | null {
  if (pw.length < 8) return 'Password must be at least 8 characters.';
  if (pw !== confirm) return 'Passwords do not match.';
  return null;
}

function extractErrorMessage(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null;
  const maybe = err as { response?: { data?: { message?: string; error?: string } } };
  return maybe.response?.data?.message ?? maybe.response?.data?.error ?? null;
}
